import {
  Button,
  DateInput,
  Modal,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../utils/dates';
import { createTemporaryUnblock } from '../api';

const keyPrefix = 'financialPortal.currentAccount.financialStatus';

interface FormValues {
  from: string;
  until: string;
}

interface Props {
  isOpen: boolean;
  studentId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TemporaryUnblockModal({
  isOpen,
  studentId,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset, getValues } = useForm<FormValues>({
    defaultValues: {
      from: '',
      until: '',
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ from: '', until: '' });
    }
  }, [isOpen, reset]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const submitUnblock = useCallback(
    async (values: FormValues) => {
      setLoading(true);
      // DateInput puede entregar Date; el API espera date-only YYYY-MM-DD.
      const result = await createTemporaryUnblock(studentId, {
        from: toDateOnlyPayload(values.from),
        until: toDateOnlyPayload(values.until),
      });
      setLoading(false);

      if (result.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${keyPrefix}.errorCreating`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${keyPrefix}.successCreating`),
      });
      handleClose();
      onSuccess();
    },
    [studentId, t, handleClose, onSuccess],
  );

  const onSubmit = useCallback(
    (values: FormValues) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${keyPrefix}.confirmTitle`),
        subtitle: t(`${keyPrefix}.confirmSubtitle`),
        btnConfirm: {
          text: t(`${keyPrefix}.confirm`),
          onConfirm: () => submitUnblock(values),
        },
        btnClose: { text: t(`${keyPrefix}.cancel`) },
      });
    },
    [t, submitUnblock],
  );

  const requiredRule = { required: t(`${keyPrefix}.required`) };
  // `raw` va `any` por la convención del repo para los `validate` de
  // react-hook-form. La regla espeja la del servidor (from <= until); el
  // "until >= hoy" lo cubre el minDate del input.
  const untilRules = {
    ...requiredRule,
    validate: (raw: any) => {
      const from = toDateOnlyPayload(getValues('from'));
      const until = toDateOnlyPayload(raw);
      if (from && until && until < from) {
        return t(`${keyPrefix}.untilBeforeFrom`) as string;
      }
      return true;
    },
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <div className="text-center mb-4">
        <h1 className="text-dark fs-22">{t(`${keyPrefix}.modalTitle`)}</h1>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} md={6} className="pb-3">
            <DateInput
              name="from"
              label={t(`${keyPrefix}.fromLabel`)}
              control={control}
              rules={requiredRule}
            />
          </Col>
          <Col xs={12} md={6} className="pb-3">
            <DateInput
              name="until"
              label={t(`${keyPrefix}.untilLabel`)}
              control={control}
              // Un "hasta" pasado nacería vencido; el servidor lo rechaza
              // igualmente con 400.
              minDate={new Date()}
              rules={untilRules}
            />
          </Col>
        </Row>
        <Row className="pt-4">
          <Col xs={12} sm={6}>
            <Button
              text={t(`${keyPrefix}.cancel`)}
              outlined
              fullwidth
              onClick={handleClose}
              disabled={loading}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Button
              text={t(`${keyPrefix}.save`)}
              fullwidth
              type="submit"
              loading={loading}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
